import { cva } from 'class-variance-authority';

import { ANIMATION_CLASSES } from '@/lib/shared-constants';
import {
  textScaleVariants,
  iconSizeVariants,
  paddingScaleVariants,
  borderRadiusVariants,
  focusRingClasses,
  disabledStateClasses,
  gapScaleVariants,
  fontWeightVariants,
  widthVariants,
  heightVariants,
} from '@/lib/shared-variants';

/**
 * FAB (Floating Action Button) styles
 * Floating action button with shadow - always requires an icon
 * Based on Figma Design System Nabat 2.0 - FAB
 */
export const fabVariants = cva(
  `inline-flex items-center justify-center ${gapScaleVariants.m} ${fontWeightVariants.medium} transition-all shadow-md ${focusRingClasses} ${disabledStateClasses} ${borderRadiusVariants.pill}`,
  {
    variants: {
      appearance: {
        solid: '',
        'outline-fill': `border`,
      },
      kind: {
        neutral: '',
        brand: '',
        danger: '',
        inverse: '',
      },
      scale: {
        s: `${paddingScaleVariants.s} ${textScaleVariants.s}`,
        m: `${paddingScaleVariants.m} ${textScaleVariants.m}`,
        l: `${paddingScaleVariants.l} ${textScaleVariants.l}`,
      },
      textEnabled: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Solid appearance variants
      {
        appearance: 'solid',
        kind: 'neutral',
        className:
          'bg-surface-subtle text-foreground hover:bg-surface-subtle/90 active:bg-surface-subtle/80',
      },
      {
        appearance: 'solid',
        kind: 'brand',
        className: 'bg-brand text-white hover:bg-brand/90 active:bg-brand/80',
      },
      {
        appearance: 'solid',
        kind: 'danger',
        className:
          'bg-status-danger text-white hover:bg-status-danger/90 active:bg-status-danger/80',
      },
      {
        appearance: 'solid',
        kind: 'inverse',
        className: 'bg-shades-basic text-white hover:bg-shades-basic/90 active:bg-shades-basic/80',
      },

      // Outline-fill appearance variants
      {
        appearance: 'outline-fill',
        kind: 'neutral',
        className:
          'bg-surface border-outline text-foreground hover:bg-surface-muted active:bg-surface-subtle',
      },
      {
        appearance: 'outline-fill',
        kind: 'brand',
        className: 'bg-surface border-brand text-brand hover:bg-brand/10 active:bg-brand/20',
      },
      {
        appearance: 'outline-fill',
        kind: 'danger',
        className:
          'bg-surface border-status-danger text-status-danger hover:bg-status-danger/10 active:bg-status-danger/20',
      },
      {
        appearance: 'outline-fill',
        kind: 'inverse',
        className: 'bg-surface border-white text-white hover:bg-white/10 active:bg-white/20',
      },

      // Icon-only (no text) - make square
      {
        textEnabled: false,
        scale: 's',
        className: `${widthVariants[6]} ${heightVariants[6]}`,
      },
      {
        textEnabled: false,
        scale: 'm',
        className: `${widthVariants[8]} ${heightVariants[8]}`,
      },
      {
        textEnabled: false,
        scale: 'l',
        className: `${widthVariants[11]} ${heightVariants[11]}`,
      },
    ],
    defaultVariants: {
      appearance: 'solid',
      kind: 'neutral',
      scale: 'm',
      textEnabled: true,
    },
  }
);

/**
 * FAB loading spinner styles
 */
export const fabSpinnerVariants = cva(ANIMATION_CLASSES.spin, {
  variants: {
    scale: {
      s: iconSizeVariants.s,
      m: iconSizeVariants.m,
      l: iconSizeVariants.l,
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});
